<div class="guided-course-creation-dialog">
  <mat-toolbar class="elearnio-mat-dialog-full-panel-class-toolbar">
    <div class="toolbar-content">
      <div class="elearnio-mat-dialog-full-panel-class-toolbar-header-title">
        <span class="headline-tertiary">{{ 'guidedCourseCreation.title' | translate }}</span>
      </div>
      <div class="elearnio-mat-dialog-full-panel-class-toolbar-header-actions">
        @if (currentPage() === 'form') {
          @if (member() && !termsAlreadyAccepted() && !isLoading()) {
            <div class="terms-checkbox">
              <mat-checkbox
                [checked]="member()?.aiTermsAgreedTo ?? false"
                (change)="onTermsAndConditionsChange($event)"
              ></mat-checkbox>
              <span
                [innerHTML]="'reusable.acceptTermsOfUse' | translate: {termsUrl: 'https://elearnio.com/de/ai-nutzungsbedingungen'}"
              ></span>
            </div>
          }
          <button
            mat-stroked-button
            color="yellow"
            class="no-border filled"
            [disabled]="!isFormValid || !member()?.aiTermsAgreedTo || isLoading()"
            (click)="onGenerate()"
          >
            {{ 'guidedCourseCreation.generateOutline' | translate }}
          </button>
        }

        @if (currentPage() === 'outline') {
          @if (!isSectionsLoading()) {
            <a class="back-button" (click)="onBack()">
              <mat-icon>chevron_left</mat-icon>
              {{ 'common.back' | translate }}
            </a>
          }
          @if (!isSectionsLoading() && !sectionsError()) {
            <button
              mat-stroked-button
              color="yellow"
              class="no-border filled"
              (click)="onCreateCourse()"
            >
              {{ 'main.profile.course.toolbar.createCourse' | translate }}
            </button>
          }
        }

        <button
          mat-stroked-button
          color="darkblue"
          class="close-btn no-padding small-icon-button"
          (click)="onClose()"
        >
          <mat-icon class="size-m">close</mat-icon>
        </button>
      </div>
    </div>
  </mat-toolbar>

  <div class="guided-course-creation-dialog-scroll">
  <div class="guided-course-creation-dialog-body">

    @if (currentPage() === 'form') {
      <form [formGroup]="form" class="guided-course-creation-dialog-form">

        <!-- Topic -->
        <section class="form-section">
          <label class="form-section-label">
            {{ 'guidedCourseCreation.topic.label' | translate }}
            <app-tooltip-button [tooltipKey]="'tooltips.guidedCourseCreation.topic'"></app-tooltip-button>
          </label>
          <textarea
            formControlName="topic"
            class="topic-textarea"
            [placeholder]="'guidedCourseCreation.topic.placeholder' | translate"
          ></textarea>
        </section>

        <!-- Language -->
        <section class="form-section">
          <label class="form-section-label">
            {{ 'guidedCourseCreation.language.label' | translate }}
            <app-tooltip-button [tooltipKey]="'tooltips.guidedCourseCreation.language'"></app-tooltip-button>
          </label>
          <mat-form-field appearance="outline" class="language-select">
            <mat-select formControlName="language">
              <mat-select-trigger>
                <app-elearnio-language-span
                  [countryCode]="form.controls.language.value"
                  [showCountryCode]="true"
                ></app-elearnio-language-span>
              </mat-select-trigger>
              @for (lang of availableLanguages; track lang) {
                <mat-option [value]="lang">
                  <app-elearnio-language-span
                    [countryCode]="lang"
                    [showCountryCode]="true"
                  ></app-elearnio-language-span>
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </section>

        <!-- Intent -->
        <section class="form-section">
          <label class="form-section-label">
            {{ 'guidedCourseCreation.intent.label' | translate }}
            <app-tooltip-button [tooltipKey]="'tooltips.guidedCourseCreation.intent'"></app-tooltip-button>
          </label>
          <mat-radio-group formControlName="intent" class="card-grid card-grid--3col">
            @for (option of intentOptions; track option.id) {
              <app-graphic-option-card
                [title]="option.titleKey | translate"
                [description]="option.descriptionKey | translate"
                [icon]="option.icon"
                [value]="option.id"
                [selected]="form.controls.intent.value === option.id"
                mode="radio"
              />
            }
          </mat-radio-group>
        </section>

        <!-- Audience -->
        <section class="form-section">
          <label class="form-section-label">
            {{ 'guidedCourseCreation.audience.label' | translate }}
            <app-tooltip-button [tooltipKey]="'tooltips.guidedCourseCreation.audience'"></app-tooltip-button>
          </label>
          <div class="card-grid card-grid--3col">
            @for (option of audienceOptions; track option.id) {
              <app-graphic-option-card
                [title]="option.titleKey | translate"
                [description]="option.descriptionKey | translate"
                [icon]="option.icon"
                [selected]="isAudienceSelected(option.id)"
                (selectionChange)="toggleAudience(option.id)"
                mode="checkbox"
              />
            }
          </div>
        </section>

      </form>
    } @else {
      <!-- Phase 2+: Summary loaded -->
      <div class="course-outline">

        <!-- Summary (always visible once fetched) -->
        <div class="course-outline-header">
          <h2 class="headline-primary">{{ outline()?.title }}</h2>
          <span class="time-badge">{{ 'guidedCourseCreation.approximately' | translate }} {{ outline()?.totalTimeMinutes }}{{ 'common.time.minuteShort' | translate }}</span>
        </div>

        <div class="course-outline-summary">
          <div class="summary-row">
            <span class="summary-label">{{ 'guidedCourseCreation.summary.language' | translate }}</span>
            <app-elearnio-language-span
              [countryCode]="form.controls.language.value"
              [showCountryCode]="true"
            ></app-elearnio-language-span>
          </div>
          <div class="summary-row">
            <span class="summary-label">{{ 'guidedCourseCreation.summary.intent' | translate }}</span>
            <span>{{ formSummary().intentLabel }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">{{ 'guidedCourseCreation.summary.audience' | translate }}</span>
            <span>{{ formSummary().audienceLabel }}</span>
          </div>
        </div>

        <p class="course-outline-description">{{ outline()?.description }}</p>

        <app-info-bubble
          [type]="infoBubbleType"
          [message]="'guidedCourseCreation.outline.editHint' | translate"
        ></app-info-bubble>

        @if (outline()?.sections?.length) {
          <mat-accordion multi>
            @for (section of outline()?.sections ?? []; track $index) {
              <mat-expansion-panel expanded>
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <h3>{{ section.headline }}</h3>
                  </mat-panel-title>
                </mat-expansion-panel-header>
                @if (section.modules.length) {
                  @for (mod of section.modules; track $index) {
                    <div class="module-tile">
                      <div class="module-tile-header">
                        <img class="module-tile-icon" [src]="mod.iconPath" />
                        <span class="module-tile-name">{{ mod.name }}</span>
                      </div>
                      <div class="module-tile-details">
                        <p>
                          <strong>{{ 'guidedCourseCreation.outline.module' | translate }}</strong>: {{ mod.moduleType | translate }}
                        </p>
                        <p>
                          <strong>{{ 'guidedCourseCreation.outline.estTime' | translate }}</strong>: {{ mod.estimatedTimeMinutes }} {{ 'common.time.minuteShort' | translate }}
                        </p>
                        <p>
                          <strong>{{ 'guidedCourseCreation.outline.whatToCover' | translate }}</strong>: {{ mod.whatToCover }}
                        </p>
                      </div>
                    </div>
                  }
                } @else if (isSectionsLoading()) {
                  <div class="course-section-loading">
                    <span class="hourglass-wrapper">
                      <mat-icon class="hourglass-top">hourglass_top</mat-icon>
                      <mat-icon class="hourglass-bottom">hourglass_bottom</mat-icon>
                    </span>
                    <span>{{ 'guidedCourseCreation.outline.working' | translate }}</span>
                  </div>
                }
              </mat-expansion-panel>
            }
          </mat-accordion>
        } @else if (isSectionsLoading()) {
          <div class="course-section-loading">
            <span class="hourglass-wrapper">
              <mat-icon class="hourglass-top">hourglass_top</mat-icon>
              <mat-icon class="hourglass-bottom">hourglass_bottom</mat-icon>
            </span>
            <span>{{ 'guidedCourseCreation.outline.working' | translate }}</span>
          </div>
        }

      </div>
    }

  </div>
  </div>
</div>
